<script setup>
import { ref, computed } from 'vue';
import { asset } from '../helpers.js';

const imgPHPointer = asset('/hkcm/images/PHPointer.png', 'public');
const imgWheelOfFortuneSimple = asset('/hkcm/images/WheelOfFortuneSimple.png', 'public');

const props = defineProps({
    totalEndPriceGross: { type: Number, required: true },
    differencePrice: { type: Number, required: true },
    minOrderValue: { type: Number, required: true }
});

const isOpen = ref(false);

const isEligible = computed(() => props.totalEndPriceGross >= props.minOrderValue);

const progress = computed(() =>
    Math.min(100, Math.round((props.totalEndPriceGross / props.minOrderValue) * 100))
);

const toCurrency = (v) =>
    typeof window !== 'undefined' && window?.Intl
        ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
        : `${v.toFixed(2)} €`;

const close = () => (isOpen.value = false);

const codeCopied = ref(false);

function copyDiscountCode() {
    navigator.clipboard.writeText('GLUECK20').then(() => {
        codeCopied.value = true;
        setTimeout(() => { codeCopied.value = false; }, 2000);
    });
}
</script>

<template>
    <!-- Cart Banner -->
    <section class="w-full rounded-2xl p-3 my-4 bg-[#EFF6FF] border border-[rgba(0,108,255,0.2)]" role="status" aria-live="polite">
        <div class="flex items-center gap-3 mb-0">
            <img :src="imgWheelOfFortuneSimple" alt="Glücksrad" class="h-14 w-14 shrink-0 object-contain" />
            <h3 class="font-bold text-base leading-tight text-[#006CFF]">
                <span v-if="isEligible">Teilnahme gesichert!</span>
                <span v-else>Noch {{ toCurrency(differencePrice) }} bis zum Dreh!</span>
            </h3>
        </div>

        <p class="text-sm text-gray-700 mb-3">
            Glücksradteilnahme ab <strong>{{ toCurrency(minOrderValue) }}</strong>
        </p>

        <!-- Progress -->
        <div class="mb-3">
            <div class="flex justify-between text-xs text-gray-600 mb-1.5">
                <span>Fortschritt</span>
                <span>{{ progress }}%</span>
            </div>
            <div class="h-[10px] w-full rounded-full overflow-hidden bg-[rgba(0,108,255,0.1)]">
                <div
                    class="h-full rounded-full transition-all duration-300 bg-[#006CFF]"
                    :style="{ width: progress + '%' }"
                ></div>
            </div>
        </div>

        <button class="text-xs underline underline-offset-2 text-[#006CFF]" @click="isOpen = true">
            Mehr dazu…
        </button>
    </section>

    <!-- Popup Modal -->
    <teleport to="body">
        <transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div
                v-if="isOpen"
                class="fixed inset-0 z-[200000] bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4"
                @click.self="close"
            >
            <div class="w-full max-w-[814px] max-h-[90vh] bg-white rounded-[20px] shadow-2xl flex flex-col overflow-hidden relative" role="dialog" aria-modal="true" aria-label="HKCM-Glücksrad 2026">
                <!-- Close Button oben rechts -->
                <button
                    class="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full text-xl text-gray-400 hover:bg-gray-100"
                    aria-label="Schließen"
                    @click="close"
                >
                    ×
                </button>

                <div class="overflow-y-auto flex flex-col gap-[30px] p-[30px]">
                    <!-- Header -->
                    <div class="flex items-center gap-0 sm:gap-1 -ml-3 sm:-ml-4 -mb-3">
                        <div class="relative h-[132px] w-[132px] sm:h-[152px] sm:w-[152px] shrink-0 overflow-hidden -mr-1 sm:mr-0">
                            <img :src="imgWheelOfFortuneSimple" alt="Das HKCM-Glücksrad" class="absolute inset-0 h-full w-full object-contain scale-[1.32]" />
                        </div>
                        <h2 class="font-bold text-[32px] leading-[120%] text-[#006CFF]">HKCM-Glücksrad 2026</h2>
                    </div>

                    <!-- Eligibility Box -->
                    <div
                        class="relative rounded-[10px] overflow-visible flex flex-col gap-3 p-3"
                        :class="
                            isEligible
                                ? 'bg-[#ECFDF5] border border-[rgba(38,117,94,0.5)]'
                                : 'bg-[#FEF2F2] border border-[rgba(220,38,38,0.4)]'
                        "
                    >
                        <div class="flex flex-col gap-3 max-w-full sm:max-w-[calc(100%-200px)]">
                            <!-- Title -->
                            <div class="flex items-center gap-3">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="shrink-0" :style="isEligible ? '' : 'filter:hue-rotate(120deg)'">
                                    <path d="M8.70985 4.11172C8.56306 3.96531 8.38175 3.85824 8.18267 3.80037C7.98358 3.74251 7.77313 3.73572 7.57073 3.78064C7.36833 3.82556 7.18051 3.92073 7.02459 4.05738C6.86867 4.19402 6.74968 4.36774 6.6786 4.5625L2.57782 15.8422C2.51045 16.0282 2.48869 16.2276 2.51436 16.4238C2.54004 16.62 2.61239 16.8071 2.72536 16.9695C2.83832 17.1319 2.9886 17.2649 3.16358 17.3572C3.33855 17.4495 3.53312 17.4985 3.73094 17.5C3.8766 17.499 4.02096 17.4726 4.15751 17.4219L15.4364 13.3203C15.6312 13.2493 15.805 13.1304 15.9418 12.9745C16.0785 12.8186 16.1737 12.6308 16.2187 12.4284C16.2637 12.226 16.257 12.0155 16.1992 11.8164C16.1414 11.6173 16.0344 11.4359 15.888 11.2891L8.70985 4.11172ZM8.05907 14.675L5.32469 11.9406L6.35282 9.11172L10.888 13.6469L8.05907 14.675ZM3.76219 16.2375L4.85594 13.2367L6.7661 15.1469L3.76219 16.2375ZM12.1872 13.175L6.82469 7.8125L7.84032 5.01016L14.9833 12.1531L12.1872 13.175ZM12.4997 5.625C12.5114 5.20248 12.6137 4.7874 12.7997 4.40781C13.2138 3.58047 13.995 3.125 14.9997 3.125C15.5231 3.125 15.8591 2.94609 16.0661 2.56172C16.1751 2.34668 16.2377 2.11111 16.2497 1.87031C16.2503 1.70455 16.3168 1.54583 16.4344 1.42906C16.5521 1.31229 16.7113 1.24703 16.877 1.24766C17.0428 1.24828 17.2015 1.31472 17.3183 1.43237C17.4351 1.55002 17.5003 1.70924 17.4997 1.875C17.4997 2.87969 16.8341 4.375 14.9997 4.375C14.4763 4.375 14.1403 4.55391 13.9333 4.93828C13.8243 5.15332 13.7617 5.38889 13.7497 5.62969C13.7494 5.71176 13.7329 5.79298 13.7012 5.86869C13.6695 5.9444 13.6232 6.01312 13.565 6.07094C13.5067 6.12876 13.4377 6.17454 13.3617 6.20567C13.2858 6.23679 13.2044 6.25265 13.1223 6.25234C13.0403 6.25204 12.9591 6.23556 12.8833 6.20387C12.8076 6.17218 12.7389 6.12588 12.6811 6.06763C12.6233 6.00937 12.5775 5.9403 12.5464 5.86436C12.5152 5.78841 12.4994 5.70708 12.4997 5.625ZM10.6247 3.125V1.25C10.6247 1.08424 10.6905 0.925268 10.8078 0.808058C10.925 0.690848 11.0839 0.625 11.2497 0.625C11.4155 0.625 11.5744 0.690848 11.6916 0.808058C11.8088 0.925268 11.8747 1.08424 11.8747 1.25V3.125C11.8747 3.29076 11.8088 3.44973 11.6916 3.56694C11.5744 3.68415 11.4155 3.75 11.2497 3.75C11.0839 3.75 10.925 3.68415 10.8078 3.56694C10.6905 3.44973 10.6247 3.29076 10.6247 3.125ZM18.5669 9.55781C18.6249 9.61588 18.6709 9.68481 18.7023 9.76066C18.7337 9.83651 18.7498 9.9178 18.7498 9.99989C18.7497 10.082 18.7335 10.1632 18.7021 10.2391C18.6706 10.3149 18.6246 10.3838 18.5665 10.4418C18.5084 10.4998 18.4395 10.5458 18.3636 10.5772C18.2878 10.6086 18.2065 10.6247 18.1244 10.6247C18.0423 10.6246 17.9611 10.6084 17.8852 10.577C17.8094 10.5455 17.7405 10.4995 17.6825 10.4414L16.4325 9.19141C16.3152 9.07413 16.2493 8.91507 16.2493 8.74922C16.2493 8.58337 16.3152 8.42431 16.4325 8.30703C16.5498 8.18976 16.7088 8.12387 16.8747 8.12387C17.0405 8.12387 17.1996 8.18976 17.3169 8.30703L18.5669 9.55781ZM18.9473 6.21797L17.0723 6.84297C16.9151 6.89539 16.7434 6.88319 16.5952 6.80906C16.4469 6.73492 16.3341 6.60492 16.2817 6.44766C16.2293 6.29039 16.2415 6.11874 16.3156 5.97047C16.3898 5.8222 16.5198 5.70945 16.677 5.65703L18.552 5.03203C18.7093 4.97961 18.8809 4.99181 19.0292 5.06594C19.1775 5.14008 19.2902 5.27008 19.3427 5.42734C19.3951 5.58461 19.3829 5.75626 19.3087 5.90453C19.2346 6.0528 19.1046 6.16555 18.9473 6.21797Z" fill="#26755E"/>
                                </svg>
                                <span class="font-bold text-[18px] leading-[130%]" :class="isEligible ? 'text-[#26755E]' : 'text-[#DC2626]'">
                                    <span v-if="isEligible">Sie sind teilnahmeberechtigt am HKCM-Glücksrad 2026!</span>
                                    <span v-else>Noch nicht teilnahmeberechtigt</span>
                                </span>
                            </div>
                            <!-- Description -->
                            <p class="text-[16px] leading-[130%] text-black">
                                Um am HKCM-Glücksrad 2026 zu drehen und einen garantierten Gewinn zu erhalten, muss Ihr Bestellwert
                                mindestens <strong>{{ toCurrency(minOrderValue) }}</strong> betragen.<br />Aktueller Warenwert:
                                <strong>{{ toCurrency(totalEndPriceGross) }}</strong
                                ><span v-if="!isEligible"
                                    >. Noch <strong>{{ toCurrency(differencePrice) }}</strong> bis zum Dreh.</span
                                >
                            </p>
                        </div>

                        <!-- PH Pointer -->
                        <img
                            :src="imgPHPointer"
                            alt=""
                            aria-hidden="true"
                            class="absolute right-3 bottom-[-10px] w-[180px] h-auto object-contain pointer-events-none hidden sm:block"
                        />
                    </div>

                    <!-- Progress (unterhalb der Box) -->
                    <div class="flex flex-col gap-2">
                        <div class="flex justify-between text-[14px] leading-[130%]">
                            <span>Fortschritt</span>
                            <span>{{ progress }}%</span>
                        </div>
                        <div class="h-[10px] w-full rounded-full overflow-hidden bg-[rgba(0,108,255,0.1)]">
                            <div
                                class="h-full rounded-full transition-all duration-300 bg-[#006CFF]"
                                :style="{ width: progress + '%' }"
                            ></div>
                        </div>
                    </div>

                    <!-- Info List -->
                    <div class="flex flex-col gap-[9px]">
                        <div class="flex items-center gap-3">
                            <span class="flex items-center justify-center w-[18px] h-[18px] shrink-0"><i class="fi fi-rr-shopping-cart text-[#006CFF] text-[16px]"></i></span>
                            <span class="text-[16px] leading-[130%]">Nach Ihrer Bestellung drehen Sie das <strong>HKCM-Glücksrad 2026</strong></span>
                        </div>

                        <div class="flex items-center gap-3">
                            <span class="flex items-center justify-center w-[18px] h-[18px] shrink-0"><i class="fi fi-rr-check-circle text-[#006CFF] text-[16px]"></i></span>
                            <span class="text-[16px] leading-[130%]">Sie gewinnen <strong>garantiert</strong> einen von <strong>8 exklusiven Preisen</strong></span>
                        </div>

                        <div class="flex items-center gap-3">
                            <span class="flex items-center justify-center w-[18px] h-[18px] shrink-0"><i class="fi fi-rr-money-bill-wave text-[#006CFF] text-[16px]"></i></span>
                            <span class="text-[16px] leading-[130%]"><strong>Sofortgewinne:</strong> Hyundai i10, Lifetime-Abo, Bargeld bis 3.000 €, Gold &amp; mehr!</span>
                        </div>

                        <div class="flex items-start gap-3">
                            <span class="flex items-center justify-center w-[18px] h-[18px] shrink-0 mt-[3px]"><i class="fi fi-rr-ticket text-[#006CFF] text-[16px]"></i></span>
                            <div class="flex flex-col gap-2 flex-1">
                                <span class="text-[16px] leading-[130%]">Zusätzliche Chance auf die Hauptpreise:</span>
                                <div class="flex flex-col gap-[8px] pl-[18px]">
                                    <div v-for="hp in ['Luxusreise auf die <strong>Malediven</strong> für 2 Personen', 'Luxuswochenende in <strong>Monaco</strong> für 2 Personen', 'Exklusive <strong>Athen</strong>-Reise für 2 Personen', 'Wine &amp; Dine Event mit <strong>Philip Hopf</strong> in Zürich für 2 Personen, inkl. Anreise &amp; Hotel', 'Adrenalin-Experience <strong>Nordschleife</strong> mit Philip Klinkmüller', 'Exklusiver <strong>Phantom Call</strong> (60 Min)']" :key="hp" class="flex items-center gap-3">
                                        <span class="flex items-center justify-center w-[14px] h-[14px] shrink-0"><i class="fi fi-rr-check text-[#006CFF] text-[13px]"></i></span>
                                        <span class="text-[16px] leading-[130%]" v-html="hp"></span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center gap-3">
                            <span class="flex items-center justify-center w-[18px] h-[18px] shrink-0"><i class="fi fi-rr-info text-[#006CFF] text-[16px]"></i></span>
                            <span class="text-[16px] leading-[130%]">Jede Bestellung = <strong>1 Dreh</strong> am Glücksrad + <strong>1 Los</strong> für die Hauptpreisverlosung</span>
                        </div>
                    </div>

                    <!-- Rabattcode Box (20 % Zusatzrabatt) -->
                    <div
                        class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 rounded-2xl px-4 sm:px-6 py-5 w-full"
                        style="background: #EFF6FF; border: 1px solid rgba(0, 108, 255, 0.25);"
                    >
                        <p style="font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 17px; line-height: 135%; color: #1e40af; letter-spacing: 0.2px">
                            Exklusive 20 % Zusatzrabatt auf alle Produkte mit dem Code:
                        </p>
                        <button
                            class="group flex items-center justify-center gap-2.5 px-4 py-2 rounded-xl transition-all duration-200 hover:scale-[1.01] active:scale-95 w-full sm:w-auto shrink-0"
                            style="background: #fff; border: 1px solid rgba(0, 108, 255, 0.4); box-shadow: 0 2px 8px rgba(0,108,255,0.1)"
                            @click="copyDiscountCode"
                        >
                            <span style="font-family: 'Montserrat', sans-serif; font-weight: 800; font-size: 17px; color: #006CFF; letter-spacing: 2px">GLUECK20</span>
                            <svg v-if="!codeCopied" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#006CFF" viewBox="0 0 256 256" class="opacity-80 group-hover:opacity-100 transition-opacity">
                                <path d="M216,32H88a8,8,0,0,0-8,8V80H40a8,8,0,0,0-8,8V216a8,8,0,0,0,8,8H168a8,8,0,0,0,8-8V176h40a8,8,0,0,0,8-8V40A8,8,0,0,0,216,32ZM160,208H48V96H160Zm48-48H176V88a8,8,0,0,0-8-8H96V48H208Z"></path>
                            </svg>
                            <span v-else style="font-size: 13px; color: #16a34a; font-weight: 600">✓ Kopiert</span>
                        </button>
                    </div>

                    <!-- Tip Box -->
                    <div class="rounded-[10px] flex flex-col gap-3 p-3 bg-[rgba(220,193,73,0.1)] border border-[rgba(220,193,73,0.2)]">
                        <div class="flex items-center gap-3">
                            <i class="fi fi-rr-bulb text-lg leading-none shrink-0 text-[#dcc149]"></i>
                            <span class="font-bold text-[18px] leading-[130%] text-[#dcc149]">Tipp</span>
                        </div>
                        <span class="text-[16px] leading-[130%]">Je häufiger Sie bestellen, desto höher Ihre Chance auf die Hauptpreise!</span>
                    </div>
                </div>
            </div>
            </div>
        </transition>
    </teleport>
</template>
